// Share without third-party code. Every option hands the text to something
// on the sender's own device: the system share sheet (Web Share API, which
// lists every installed chat app), a chat app through its own URL scheme
// (whatsapp:, tg:), the mail app (mailto:) or the messages app (sms:).
//
// Never the web share links (wa.me/?text=…, t.me/share/url?url=…): those
// carry the whole link, #key included, to that company's web server as a
// URL, where it is logged before the chat app ever encrypts it. The app
// schemes go straight to the installed app; without one, the click does
// nothing and the page says to copy and paste instead.

/** mailto: with a subject and body (RFC 6068: encode everything, line breaks as CRLF). */
export const mailtoHref = (subject: string, body: string): string =>
  `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body.replace(/\r?\n/g, '\r\n'))}`;

/** sms: with a body. `?&body=` is the form both iOS and Android read. */
export const smsHref = (body: string): string => `sms:?&body=${encodeURIComponent(body)}`;

/** WhatsApp's app scheme (phones, and WhatsApp Desktop on Windows and macOS). */
export const whatsappHref = (text: string): string => `whatsapp://send?text=${encodeURIComponent(text)}`;

/** Telegram's app scheme. It needs a URL; the text goes with it. */
export const telegramHref = (url: string, text: string): string => `tg://msg_url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;

export interface ShareData {
  title?: string;
  text: string;
  url?: string;
}

/** Whether this browser can open the system share sheet with `data`. */
export const canShareNatively = (data: ShareData, nav: Navigator | undefined = typeof navigator === 'undefined' ? undefined : navigator): boolean =>
  typeof nav?.share === 'function' && (typeof nav.canShare !== 'function' || nav.canShare(data));

export type ShareResult = 'shared' | 'cancelled' | 'failed';

/** Open the system share sheet. Closing it without choosing an app is "cancelled", not an error. */
export const shareNatively = async (data: ShareData, nav: Navigator = navigator): Promise<ShareResult> => {
  try {
    await nav.share(data);
    return 'shared';
  } catch (err) {
    return err instanceof DOMException && err.name === 'AbortError' ? 'cancelled' : 'failed';
  }
};
